import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import Modal from 'components/ui/Modal';
import AfterSalesSummaryBox from 'pages/BookingDetails/AfterSalesSummaryBox';
import { legShape, productsShape } from 'constants/propTypes';
import classOfService from 'constants/propTypes/classOfService';

import { showNewFaresConditionsOnExchange, showNCONonRefundableRules } from 'utils/booking';
import FareConditions from '../FareConditions';

import {
  Container,
  FareDetails,
  ClassOfService,
  SeeMore,
  StyledNewFaresConditions,
  StyledContainer,
  StyledFareSummary
} from './FareDetailsContainer.style';
import { showModalFareConditions } from '../FareConditions/utils';
import ShowFareConditions from '../ShowFareConditions';
import { getUniqueProductFamilies } from './FareDetailsContainer.utils';

import Tabs from 'components/ui/Tabs';

const FareDetailsContainer = ({ includeFareConditions, classOfService, serviceType, journey, currency, withExchangedProducts }) => {
  const [showFareConditions, setShowFareConditions] = useState(false);
  const [showFareConditionModal, setShowFareConditionModal] = useState(false);

  const handleToggleFareConditions = () => {
    setShowFareConditions((e) => !e);
  };

  const handleToggleFareConditionModal = () => {
    setShowFareConditionModal((e) => !e);
  };

  const newFaresConditions = showNewFaresConditionsOnExchange({ products: journey?.products });
  const isModalFareConditionsEnabled = showModalFareConditions({ journey });

  const nonCancelledBookingItem = journey?.bookingItems?.find(({ cancelled, provisional }) => !cancelled && !provisional);
  const bookingItem = nonCancelledBookingItem || journey?.bookingItems?.[0];
  const activeCancelRule = bookingItem?.afterSalesRules?.filter(({ type, currentActive }) => type === 'CANCEL' && currentActive);

  const refundableNCORules = showNCONonRefundableRules({
    products: journey?.products,
    withExchangedProducts,
    departureDate: journey?.timing?.isoDate,
    activeCancelRule: activeCancelRule,
    bookingItemPrice: bookingItem?.price,
    classOfServiceCode: classOfService.code,
    serviceTypeCode: serviceType
  });
  const doNotShowNonRefundableMessage = bookingItem?.canBeCancelled && bookingItem?.canBeRefunded;

  const productFamilies = journey?.products?.map(({ productFamily }) => productFamily);
  const uniqueProductFamilies = getUniqueProductFamilies({ productFamilies });
  const tabsList = uniqueProductFamilies.map((e) => ({ value: e?.familyGroupName, label: e?.familyGroupName }));
  const [selectedTab, setSelectedTab] = useState(tabsList[0]?.value);

  return (
    <Container classOfServiceCode={classOfService.code} data-cy="fare-details">
      <FareDetails>
        <StyledContainer>
          {includeFareConditions && (
            <ShowFareConditions
              showFareConditions={showFareConditions}
              uniqueProductFamilies={uniqueProductFamilies}
              handleOnClick={handleToggleFareConditions}
            />
          )}
          {newFaresConditions && (
            <StyledNewFaresConditions>
              <FormattedMessage id="nco.ExchangesNewFareConditions" />
            </StyledNewFaresConditions>
          )}
          {!doNotShowNonRefundableMessage && refundableNCORules && (
            <StyledNewFaresConditions>
              <FormattedMessage id="afterSalesRule.cancel.ncoUnavailable" />
            </StyledNewFaresConditions>
          )}
        </StyledContainer>
        <ClassOfService classOfServiceCode={classOfService.code}>{classOfService.name}</ClassOfService>
      </FareDetails>

      {showFareConditions && Boolean(journey) ? (
        <StyledFareSummary>
          {tabsList.length > 1 && <Tabs selectedValue={selectedTab} tabs={tabsList} onClick={setSelectedTab} />}
          <AfterSalesSummaryBox
            direction={journey}
            currency={currency}
            refundableNCORules={refundableNCORules}
            selectedProduct={selectedTab}
          />
          {isModalFareConditionsEnabled && (
            <SeeMore data-cy="toggle-fare-condition-modal" margin="4px 0" type="button" onClick={handleToggleFareConditionModal}>
              <FormattedMessage id="journeySummary.seeMore" />
            </SeeMore>
          )}
        </StyledFareSummary>
      ) : null}
      <Modal
        minWidth="auto"
        isOpen={showFareConditionModal}
        onClose={handleToggleFareConditionModal}
        title={<FormattedMessage id="refunds.fareConditions" values={{ productFamilyCode: `(${selectedTab})` || '' }} />}
        bottomRow={<FareConditions journey={journey} currency={currency} selectedProduct={selectedTab} />}
      />
    </Container>
  );
};

FareDetailsContainer.propTypes = {
  includeFareConditions: PropTypes.bool,
  classOfService: classOfService,
  serviceType: PropTypes.string,
  currency: PropTypes.string,
  journey: legShape,
  withExchangedProducts: PropTypes.arrayOf(
    PropTypes.shape({
      products: productsShape
    })
  )
};

export default FareDetailsContainer;
